'use client';

import dynamic from 'next/dynamic';
import { Fragment, useState } from 'react';
import logCellCoverageCalloutTapped from '@alltrails/amplitude/events/logCellCoverageCalloutTapped';
import LinkOrNativeAnchor from '@alltrails/denali/components/LinkOrNativeAnchor';
import TableRow from '@alltrails/denali/components/TableRow';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailHighlight from '@alltrails/types/TrailHighlight';
import getTrailHighlightIcon from './getTrailHighlightIcon';
import styles from './TrailHighlights.module.scss';

const SpottyServiceModal = dynamic(() => import('./SpottyServiceModal'), { ssr: false });
const InstallModal = dynamic(() => import('@alltrails/install-prompt/components/InstallModal'), { ssr: false });

type TrailHighlightsProps = {
  highlights: TrailHighlight[];
  trailId: TrailId;
};

const TrailHighlights = ({ highlights, trailId }: TrailHighlightsProps) => {
  const [isSpottyServiceOpen, setIsSpottyServiceOpen] = useState(false);
  const [isInstallOpen, setIsInstallOpen] = useState(false);

  const openSpottyService = () => {
    logCellCoverageCalloutTapped({ trail_id: trailId });
    setIsSpottyServiceOpen(true);
  };

  const openInstallFromSpottyService = () => {
    setIsSpottyServiceOpen(false);
    setIsInstallOpen(true);
  };

  return (
    <div className={styles.container}>
      {highlights.map(highlight => {
        const Icon = getTrailHighlightIcon(highlight.type);
        const isLowCellService = highlight.type === 'info_low-cell-service';
        const row = (
          <TableRow
            className={styles.row}
            label={<span className={styles.title}>{highlight.title}</span>}
            description={highlight.subtitle}
            leftContent={<Icon />}
            rightContent={highlight.slug || isLowCellService ? <ChevronSm orientation="right" color="--color-text-primary" /> : undefined}
          />
        );

        if (isLowCellService) {
          return (
            <button key={highlight.type} type="button" className={styles.button} onClick={openSpottyService}>
              {row}
            </button>
          );
        }

        if (highlight.slug) {
          return (
            <LinkOrNativeAnchor key={highlight.type} href={`/${highlight.slug}`} className={styles.link}>
              {row}
            </LinkOrNativeAnchor>
          );
        }

        return <Fragment key={highlight.type}>{row}</Fragment>;
      })}
      {isSpottyServiceOpen && (
        <SpottyServiceModal
          isOpen={isSpottyServiceOpen}
          onCloseRequest={() => setIsSpottyServiceOpen(false)}
          onDownloadInApp={openInstallFromSpottyService}
          trailId={trailId}
        />
      )}
      {isInstallOpen && <InstallModal isOpen={isInstallOpen} onCloseRequest={() => setIsInstallOpen(false)} />}
    </div>
  );
};

export default TrailHighlights;
